import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Flame, Star, Medal, Target, Gift, Clock, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

import { API } from '../config/api';

const LEVEL_ICONS = {
  'Rookie': '🌱',
  'Bronze': '🥉',
  'Silver': '🥈',
  'Gold': '🥇',
  'Platinum': '💎',
  'Diamond': '💠',
  'Elite': '🔥'
};

export default function GamificationLeaderboard({ currentChildId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [timeRange, setTimeRange] = useState('all');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('leaderboard'); // leaderboard, challenges, activity
  const [challenges, setChallenges] = useState(null);
  const [activities, setActivities] = useState([]);
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState('');
  const [claimingChallenge, setClaimingChallenge] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/gamification/leaderboard?time_range=${timeRange}&limit=20`;
      if (selectedSport) {
        url = `${API}/gamification/sports-leaderboard?sport=${encodeURIComponent(selectedSport)}&limit=20`;
      }
      const res = await axios.get(url);
      setLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  }, [timeRange, selectedSport]);

  const fetchChallenges = useCallback(async () => {
    if (!currentChildId) return;
    try {
      const res = await axios.get(`${API}/gamification/weekly-challenges/${currentChildId}`);
      setChallenges(res.data);
    } catch (err) {
      console.error('Failed to fetch challenges');
    }
  }, [currentChildId]);

  const fetchActivities = useCallback(async () => {
    if (!currentChildId) return;
    try {
      const res = await axios.get(`${API}/gamification/activity-feed/${currentChildId}?limit=15`);
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error('Failed to fetch activities');
    }
  }, [currentChildId]);

  const fetchSports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gamification/available-sports`);
      setSports(res.data.sports || []);
    } catch (err) {
      console.error('Failed to fetch sports');
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
    fetchSports();
  }, [fetchLeaderboard, fetchSports]);

  useEffect(() => {
    if (view === 'challenges') {
      fetchChallenges();
    } else if (view === 'activity') {
      fetchActivities();
    }
  }, [view, fetchChallenges, fetchActivities]);

  const claimReward = async (challengeId) => {
    setClaimingChallenge(challengeId);
    try {
      const res = await axios.post(`${API}/gamification/claim-challenge-reward?child_id=${currentChildId}&challenge_id=${challengeId}`);
      toast.success(res.data.message);
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim reward');
    } finally {
      setClaimingChallenge(null);
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500 to-amber-400 text-black';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-black';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return 'bg-zinc-700 text-zinc-300';
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-zinc-800 rounded w-48 mb-4" />
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-14 bg-zinc-800 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="gamification-leaderboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500/20 to-[#ff025f]/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-[#ff025f] rounded-full flex items-center justify-center">
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Gamification</h3>
              <p className="text-xs text-zinc-400">Compete, earn rewards & climb ranks</p>
            </div>
          </div>
        </div>
        
        {/* View Tabs */}
        <div className="flex gap-1 mt-3 bg-zinc-800/50 rounded-lg p-1">
          {[
            { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
            { id: 'challenges', label: 'Challenges', icon: Target },
            { id: 'activity', label: 'Activity', icon: Clock }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-all ${
                view === tab.id 
                  ? 'bg-[#02b5ff] text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard View */}
      {view === 'leaderboard' && (
        <>
          {/* Filters */}
          <div className="p-3 border-b border-zinc-800 flex flex-wrap gap-2">
            {/* Time Range */}
            <div className="flex bg-zinc-800/50 rounded-lg p-0.5">
              {['all', 'month', 'week'].map((range) => (
                <button
                  key={range}
                  onClick={() => { setTimeRange(range); setSelectedSport(''); }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                    timeRange === range && !selectedSport
                      ? 'bg-zinc-700 text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {range === 'all' ? 'All Time' : range === 'month' ? 'Month' : 'Week'}
                </button>
              ))}
            </div>
            
            {/* Sport Filter */}
            {sports.length > 0 && (
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1 text-xs text-white"
              >
                <option value="">All Sports</option>
                {sports.map(sport => (
                  <option key={sport.name} value={sport.name}>
                    {sport.name} ({sport.athlete_count})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Leaderboard List */}
          <div className="divide-y divide-zinc-800">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin mx-auto" />
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="p-8 text-center">
                <Trophy className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500">No rankings yet</p>
                <p className="text-xs text-zinc-600">Start earning points to appear on the leaderboard!</p>
              </div>
            ) : (
              leaderboard.map((entry) => {
                const isCurrentUser = entry.child_id === currentChildId;
                
                return (
                  <div 
                    key={entry.child_id}
                    className={`p-3 flex items-center gap-3 transition-all ${
                      isCurrentUser ? 'bg-[#02b5ff]/10' : 'hover:bg-zinc-800/30'
                    }`}
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getRankStyle(entry.rank)}`}>
                      {entry.rank}
                    </div>
                    
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-700 flex items-center justify-center">
                      {entry.photo_url ? (
                        <img src={entry.photo_url} alt={entry.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-bold text-zinc-400">
                          {entry.name?.charAt(0) || '?'}
                        </span>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold truncate ${isCurrentUser ? 'text-[#02b5ff]' : 'text-white'}`}>
                          {entry.name}
                        </span>
                        {isCurrentUser && (
                          <span className="text-[10px] bg-[#02b5ff] text-white px-1.5 py-0.5 rounded">YOU</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-zinc-500">{entry.sport || 'Multi-sport'}</span>
                        <span style={{ color: entry.level?.color }}>
                          {LEVEL_ICONS[entry.level?.name]} {entry.level?.name}
                        </span>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="text-right">
                      <div className="font-bold text-white">
                        {(timeRange !== 'all' && !selectedSport ? entry.period_points : entry.total_points || 0).toLocaleString()}
                        <span className="text-xs text-zinc-500 ml-1">pts</span>
                      </div>
                      {entry.streak > 0 && (
                        <div className="flex items-center gap-1 text-xs text-orange-500 justify-end">
                          <Flame className="h-3 w-3" />
                          <span>{entry.streak} day streak</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Challenges View */}
      {view === 'challenges' && (
        <div className="p-4">
          {!currentChildId ? (
            <div className="text-center py-8">
              <Target className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">Select an athlete to see challenges</p>
            </div>
          ) : !challenges ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {/* Time Remaining */}
              <div className="flex items-center justify-between mb-4 p-3 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#02b5ff]" />
                  <span className="text-sm text-zinc-300">Weekly Challenges</span>
                </div>
                <span className="text-xs text-zinc-400">
                  {challenges.days_remaining}d {challenges.hours_remaining}h remaining
                </span>
              </div>

              {/* Challenge Cards */}
              <div className="space-y-3">
                {challenges.challenges.map(challenge => (
                  <div 
                    key={challenge.id}
                    className={`p-4 rounded-xl border transition-all ${
                      challenge.completed 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : 'bg-zinc-800/30 border-zinc-700'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: `${challenge.color}20` }}
                      >
                        {challenge.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white">{challenge.name}</h4>
                          {challenge.completed && !challenge.reward_claimed && (
                            <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded animate-pulse">
                              READY
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-400 mt-0.5">{challenge.description}</p>
                        
                        {/* Progress Bar */}
                        <div className="mt-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-zinc-500">
                              {challenge.current} / {challenge.target_value}
                            </span>
                            <span className="text-[#02b5ff]">+{challenge.reward_points} pts</span>
                          </div>
                          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${challenge.progress_percent}%`,
                                backgroundColor: challenge.completed ? '#22c55e' : challenge.color
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Claim Button */}
                      {challenge.completed && !challenge.reward_claimed && (
                        <Button
                          size="sm"
                          onClick={() => claimReward(challenge.id)}
                          disabled={claimingChallenge === challenge.id}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          {claimingChallenge === challenge.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Gift className="h-4 w-4 mr-1" />
                              Claim
                            </>
                          )}
                        </Button>
                      )}
                      {challenge.reward_claimed && (
                        <span className="text-xs text-green-500 font-medium">✓ Claimed</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Activity View */}
      {view === 'activity' && (
        <div className="divide-y divide-zinc-800">
          {!currentChildId ? (
            <div className="p-8 text-center">
              <Clock className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">Select an athlete to see activity</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="p-8 text-center">
              <Clock className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No recent activity</p>
              <p className="text-xs text-zinc-600">Start earning points to see your activity here!</p>
            </div>
          ) : (
            activities.map((activity, idx) => (
              <div key={idx} className="p-3 flex items-center gap-3 hover:bg-zinc-800/30">
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-lg">
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">
                    {activity.description || activity.activity_type.replace(/_/g, ' ')}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {new Date(activity.created_at).toLocaleDateString(undefined, { 
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                    })}
                  </p>
                </div>
                <div className="text-sm font-bold text-[#02b5ff]">
                  +{activity.points_earned}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Level Legend */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
        <div className="flex items-center gap-1 flex-wrap justify-center text-xs">
          {Object.entries(LEVEL_ICONS).map(([name, icon]) => (
            <span key={name} className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">
              {icon} {name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
